import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check, Plus } from 'lucide-react';
import useModalBack from '../../hooks/useModalBack';

/**
 * A select component that shows a grid of options on mobile and regular dropdown on desktop
 * @param {string} value - Current selected value
 * @param {function} onChange - Callback when value changes
 * @param {Array} options - Array of { value, label, icon?, iconColor?, description?, group? }
 * @param {Array} groups - Optional ordered group names. Options are grouped by their `group` field.
 *                         Options without a group go under "Other". If not provided, flat grid is shown.
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Label for the select (shown in mobile modal header)
 * @param {number} columns - Number of columns in mobile grid (default: 2)
 * @param {string} size - Size of options: 'sm', 'md' (default), 'lg'
 * @param {string} className - Additional classes for the trigger button
 * @param {boolean} error - Show error state
 * @param {boolean} showIconInTrigger - Show selected icon in trigger button
 * @param {object} actionButton - Optional action button { label, onClick } shown at the end
 */
export default function MobileSelect({
  value,
  onChange,
  options = [],
  groups = null,
  placeholder = 'Select...',
  label = 'Select',
  columns = 2,
  size = 'md',
  className = '',
  disabled = false,
  error = false,
  showIconInTrigger = false,
  actionButton = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = useCallback(() => setIsOpen(false), []);

  useModalBack(isOpen, closeModal, `Select:${label}`);

  const selectedOption = options.find(opt => opt.value === value);
  const SelectedIcon = selectedOption?.icon;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleActionClick = () => {
    setIsOpen(false);
    actionButton?.onClick?.();
  };

  const gridColsClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4'
  }[columns] || 'grid-cols-2';

  const sizeClasses = {
    sm: {
      padding: 'p-2.5',
      iconWrapper: 'w-8 h-8 mb-1.5',
      icon: 'w-4 h-4',
      label: 'text-xs',
      gap: 'gap-2',
      checkmark: 'w-4 h-4 top-1 right-1',
      checkIcon: 'w-2.5 h-2.5'
    },
    md: {
      padding: 'p-3',
      iconWrapper: 'w-9 h-9 mb-1.5',
      icon: 'w-4.5 h-4.5',
      label: 'text-sm',
      gap: 'gap-2.5',
      checkmark: 'w-4 h-4 top-1.5 right-1.5',
      checkIcon: 'w-2.5 h-2.5'
    },
    lg: {
      padding: 'p-4',
      iconWrapper: 'w-10 h-10 mb-2',
      icon: 'w-5 h-5',
      label: 'text-sm',
      gap: 'gap-3',
      checkmark: 'w-5 h-5 top-2 right-2',
      checkIcon: 'w-3 h-3'
    }
  }[size] || sizeClasses.md;

  // Build grouped options if groups are provided
  const groupedOptions = groups ? (() => {
    const grouped = [];
    const used = new Set();

    for (const groupName of groups) {
      const groupItems = options.filter(o => o.group === groupName);
      if (groupItems.length > 0) {
        grouped.push({ name: groupName, items: groupItems });
        groupItems.forEach(o => used.add(o.value));
      }
    }

    // Remaining ungrouped options
    const remaining = options.filter(o => !used.has(o.value));
    if (remaining.length > 0) {
      grouped.push({ name: 'Other', items: remaining });
    }

    return grouped;
  })() : null;

  const renderOption = (opt) => {
    const isSelected = opt.value === value;
    const Icon = opt.icon;
    const iconColor = opt.iconColor;

    return (
      <button
        key={opt.value}
        type="button"
        onClick={() => handleSelect(opt.value)}
        className={`relative flex flex-col items-center justify-center ${sizeClasses.padding} rounded-xl border-2 transition-all ${
          isSelected
            ? 'border-primary-500 bg-primary-50'
            : 'border-surface-200 bg-white hover:border-surface-300 hover:bg-surface-50'
        }`}
      >
        {isSelected && (
          <div className={`absolute ${sizeClasses.checkmark} bg-primary-500 rounded-full flex items-center justify-center`}>
            <Check className={`${sizeClasses.checkIcon} text-white`} />
          </div>
        )}

        {Icon && (
          <div
            className={`${sizeClasses.iconWrapper} rounded-xl flex items-center justify-center`}
            style={{
              backgroundColor: iconColor ? `${iconColor}20` : (isSelected ? 'rgb(219 234 254)' : 'rgb(241 245 249)')
            }}
          >
            <Icon
              className={sizeClasses.icon}
              style={{ color: iconColor || (isSelected ? '#2563eb' : '#64748b') }}
            />
          </div>
        )}

        <span className={`${sizeClasses.label} font-medium text-center leading-tight ${
          isSelected ? 'text-primary-700' : 'text-surface-700'
        }`}>
          {opt.label}
        </span>

        {opt.description && (
          <span className="text-xs text-surface-500 mt-0.5 text-center">
            {opt.description}
          </span>
        )}
      </button>
    );
  };

  const renderActionButton = () => {
    if (!actionButton) return null;
    return (
      <button
        type="button"
        onClick={handleActionClick}
        className={`flex flex-col items-center justify-center ${sizeClasses.padding} rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 hover:border-primary-400 hover:bg-primary-100 transition-all`}
      >
        <div className={`${sizeClasses.iconWrapper} rounded-xl flex items-center justify-center bg-primary-100`}>
          <Plus className={`${sizeClasses.icon} text-primary-600`} />
        </div>
        <span className={`${sizeClasses.label} font-medium text-center leading-tight text-primary-700`}>
          {actionButton.label}
        </span>
      </button>
    );
  };

  return (
    <>
      {/* Trigger Button */}
      <div className="relative">
        {/* Desktop: Native select or custom dropdown with action */}
        {actionButton ? (
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(true)}
            className={`hidden xl:flex w-full items-center justify-between px-4 py-3 border rounded-xl bg-white transition-colors ${
              error ? 'border-expense-500' : 'border-surface-200'
            } ${
              disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-surface-300'
            } ${className}`}
            disabled={disabled}
          >
            <div className="flex items-center gap-2 min-w-0">
              {showIconInTrigger && SelectedIcon && (
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: selectedOption?.iconColor ? `${selectedOption.iconColor}20` : undefined,
                    background: !selectedOption?.iconColor ? 'rgb(241 245 249)' : undefined
                  }}
                >
                  <SelectedIcon
                    className="w-4 h-4"
                    style={{ color: selectedOption?.iconColor || '#64748b' }}
                  />
                </div>
              )}
              <span className={`truncate ${selectedOption ? 'text-surface-900' : 'text-surface-400'}`}>
                {selectedOption?.label || placeholder}
              </span>
            </div>
            <ChevronDown className="w-5 h-5 text-surface-400 flex-shrink-0" />
          </button>
        ) : (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`hidden xl:block input ${error ? 'input-error' : ''} ${className}`}
            disabled={disabled}
          >
            {!value && <option value="">{placeholder}</option>}
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {/* Mobile: Custom trigger */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(true)}
          className={`xl:hidden w-full h-[46px] flex items-center justify-between px-4 border rounded-xl bg-white transition-colors overflow-hidden ${
            error ? 'border-expense-500' : 'border-surface-200'
          } ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-surface-300'
          } ${className}`}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            {showIconInTrigger && SelectedIcon && (
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: selectedOption?.iconColor ? `${selectedOption.iconColor}20` : undefined,
                  background: !selectedOption?.iconColor ? 'rgb(241 245 249)' : undefined
                }}
              >
                <SelectedIcon
                  className="w-4 h-4"
                  style={{ color: selectedOption?.iconColor || '#64748b' }}
                />
              </div>
            )}
            <span className={`truncate ${selectedOption ? 'text-surface-900' : 'text-surface-400'}`}>
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <ChevronDown className="w-5 h-5 text-surface-400 flex-shrink-0" />
        </button>
      </div>

      {/* Modal */}
      {isOpen && createPortal(
        <div
          className={`fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex z-[100] ${
            actionButton
              ? 'items-end md:items-center justify-center md:p-4'
              : 'xl:hidden items-end justify-center'
          }`}
          onClick={() => setIsOpen(false)}
        >
          <div
            className={`bg-white shadow-2xl w-full flex flex-col animate-slide-up ${
              actionButton
                ? 'rounded-t-2xl md:rounded-2xl md:max-w-md max-h-[80vh]'
                : 'rounded-t-2xl max-h-[70vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
              <h3 className="text-lg font-semibold text-surface-900">{label}</h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Options */}
            <div className="flex-1 overflow-y-auto p-3">
              {groupedOptions ? (
                // Grouped layout
                <div className="space-y-4">
                  {groupedOptions.map((group) => (
                    <div key={group.name}>
                      <h4 className="text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2 px-1">
                        {group.name}
                      </h4>
                      <div className={`grid ${gridColsClass} ${sizeClasses.gap}`}>
                        {group.items.map(renderOption)}
                        {/* Show action button only in last group */}
                        {group === groupedOptions[groupedOptions.length - 1] && renderActionButton()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Flat grid layout
                <div className={`grid ${gridColsClass} ${sizeClasses.gap}`}>
                  {options.map(renderOption)}
                  {renderActionButton()}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
